import { Checkbox } from '@ardoq/forms';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { Label, ListItem, IconWrapper, CountWrapper } from './atoms';
import { ComponentType, ComponentTypeProps } from './ComponentType';
import { WithPopover, TruncatedTextPopover } from '@ardoq/popovers';
import { DirectedTriple, ReferenceDirection } from '@ardoq/api-types';
import { TripleOptions } from 'viewpointBuilder/types';
import { Tag } from '@ardoq/status-ui';
import { FlexBox } from '@ardoq/layout';
import { useId } from 'react';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';

type OptionProps = Omit<TripleOptions['outgoing'][0], 'countKey'> & {
  // TODO (chriskr): investigate non-nullable here
  metaDataComponentSelected: TripleOptions['selectedNode']['metaDataComponent'];
  direction: ReferenceDirection;
  toggleOption: (
    tripleId: string,
    direction: ReferenceDirection,
    namedDirectedTriple: DirectedTriple,
    isSelected: boolean
  ) => void;
  isDisabledWholeRow: boolean;
  i18nLocale: I18nLocale;
};

type OptionTogglerProps = {
  togglingDisabledExplanation?: string;
  isSelected: boolean;
  isDisabled: boolean;
  ariaLabelledby: string;
  onToggle: () => void;
};

const OptionToggler = ({
  ariaLabelledby,
  isSelected,
  isDisabled,
  onToggle,
  togglingDisabledExplanation,
}: OptionTogglerProps) => (
  <WithPopover content={togglingDisabledExplanation}>
    <span>
      <Checkbox
        aria-labelledby={ariaLabelledby}
        isChecked={isSelected}
        isDisabled={isDisabled}
        onChange={onToggle}
        stopPropagation={true}
      />
    </span>
  </WithPopover>
);

export const OutgoingTripleOption = ({
  tripleId,
  metaDataComponent,
  metaDataReference,
  isSelected,
  isDisabled,
  direction,
  namedDirectedTriple,
  toggleOption,
  metaDataComponentSelected: {
    representationData: representationDataSelectedNode,
    label: labelSelectedNode,
  },
  instanceCounts,
  togglingDisabledExplanation,
  isDisabledWholeRow,
  i18nLocale,
}: OptionProps) => {
  const { representationData, label } = metaDataComponent;
  const id = useId();
  return (
    <ListItem
      id={id}
      data-test-id={`outgoing-triple-option-${namedDirectedTriple.sourceType}-${namedDirectedTriple.referenceType}-${namedDirectedTriple.targetType}`}
      $isClickable={!isDisabled}
      $isDisabled={isDisabledWholeRow && isDisabled}
      key={tripleId}
      onClick={event => {
        event.preventDefault();

        if (!isDisabled) {
          toggleOption(tripleId, direction, namedDirectedTriple, !isSelected);
        }
      }}
    >
      <WithPopover content={togglingDisabledExplanation}>
        <FlexBox align="center" paddingLeft="small">
          <OptionToggler
            ariaLabelledby={id}
            togglingDisabledExplanation={togglingDisabledExplanation}
            isDisabled={isDisabled}
            isSelected={isSelected}
            onToggle={() => {
              toggleOption(
                tripleId,
                direction,
                namedDirectedTriple,
                !isSelected
              );
            }}
          />
        </FlexBox>
        <OutgoingTripleRepresentation
          contextRepresentationData={representationDataSelectedNode}
          contextLabel={labelSelectedNode}
          representationData={representationData}
          label={label}
          metaDataReference={metaDataReference.representationData}
          i18nLocale={i18nLocale}
        />
        <FlexBox align="center" paddingRight="small">
          {instanceCounts !== null ? (
            <Tag>
              <CountWrapper>{instanceCounts}</CountWrapper>
            </Tag>
          ) : null}
        </FlexBox>
      </WithPopover>
    </ListItem>
  );
};

export type TripleRepresentationProps = {
  contextRepresentationData: ComponentTypeProps['representationData'];
  contextLabel: string;
  representationData: ComponentTypeProps['representationData'];
  label: ComponentTypeProps['label'];
  metaDataReference: TripleOptions['outgoing'][0]['metaDataReference']['representationData'];
};

export const OutgoingTripleRepresentation = ({
  contextRepresentationData,
  contextLabel,
  representationData,
  label,
  metaDataReference,
  i18nLocale,
}: TripleRepresentationProps & WithI18nLocale) => (
  <FlexBox
    gap="xsmall"
    align="center"
    flex={1}
    minWidth="0"
    aria-label={t(
      i18nLocale,
      'viewpointBuilder:tripleOption.outgoingAriaLabel',
      {
        contextLabel,
        referenceLabel: metaDataReference?.displayLabel ?? '',
        label,
      },
      'Outgoing {{contextLabel}} {{referenceLabel}} {{label}}'
    )}
  >
    <ComponentTypeWithoutLabel
      representationData={contextRepresentationData}
      isSelected={true}
      label={contextLabel}
    />
    <Icon iconName={IconName.ARROW_FORWARD} iconSize={IconSize.MEDIUM} />
    <Label>
      <TruncatedTextPopover>
        {metaDataReference?.displayLabel}
      </TruncatedTextPopover>
    </Label>
    <Icon iconName={IconName.ARROW_FORWARD} iconSize={IconSize.MEDIUM} />
    <ShrinkableComponentType
      representationData={representationData}
      label={label}
    />
  </FlexBox>
);

export const IncomingTripleOption = ({
  tripleId,
  metaDataComponent,
  metaDataReference,
  isSelected,
  isDisabled,
  direction,
  toggleOption,
  metaDataComponentSelected: {
    representationData: representationDataSelectedNode,
    label: labelSelectedNode,
  },
  namedDirectedTriple,
  instanceCounts,
  togglingDisabledExplanation,
  isDisabledWholeRow,
  i18nLocale,
}: OptionProps) => {
  const { representationData, label } = metaDataComponent;
  const id = useId();
  return (
    <ListItem
      id={id}
      $isClickable={!isDisabled}
      $isDisabled={isDisabledWholeRow && isDisabled}
      key={tripleId}
      data-test-id={`incoming-triple-option-${namedDirectedTriple.sourceType}-${namedDirectedTriple.referenceType}-${namedDirectedTriple.targetType}`}
      onClick={event => {
        event.preventDefault();
        if (!isDisabled) {
          toggleOption(tripleId, direction, namedDirectedTriple, !isSelected);
        }
      }}
    >
      <WithPopover content={togglingDisabledExplanation}>
        <FlexBox align="center" paddingLeft="small">
          <OptionToggler
            ariaLabelledby={id}
            togglingDisabledExplanation={togglingDisabledExplanation}
            isDisabled={isDisabled}
            isSelected={isSelected}
            onToggle={() =>
              toggleOption(
                tripleId,
                direction,
                namedDirectedTriple,
                !isSelected
              )
            }
          />
        </FlexBox>

        <FlexBox
          gap="xsmall"
          align="center"
          flex={1}
          minWidth="0"
          aria-label={t(
            i18nLocale,
            'viewpointBuilder:tripleOption.incomingAriaLabel',
            {
              label,
              referenceLabel:
                metaDataReference.representationData?.displayLabel ?? '',
              contextLabel: labelSelectedNode,
            },
            'Incoming {{label}} {{referenceLabel}} {{contextLabel}}'
          )}
        >
          <ShrinkableComponentType
            representationData={representationData}
            label={label}
          />
          <Icon iconName={IconName.ARROW_FORWARD} iconSize={IconSize.MEDIUM} />
          <Label>
            <TruncatedTextPopover>
              {metaDataReference.representationData?.displayLabel}
            </TruncatedTextPopover>
          </Label>
          <Icon iconName={IconName.ARROW_FORWARD} iconSize={IconSize.MEDIUM} />
          <ComponentTypeWithoutLabel
            representationData={representationDataSelectedNode}
            isSelected={true}
            label={labelSelectedNode}
          />
        </FlexBox>

        <FlexBox align="center" paddingRight="small">
          {instanceCounts !== null ? (
            <Tag>
              <CountWrapper>{instanceCounts}</CountWrapper>
            </Tag>
          ) : null}
        </FlexBox>
      </WithPopover>
    </ListItem>
  );
};

export const IncomingTripleRepresentation = ({
  contextRepresentationData,
  contextLabel,
  representationData,
  label,
  metaDataReference,
}: TripleRepresentationProps) => (
  <FlexBox gap="xsmall" align="center" flex={1} minWidth="0">
    <ShrinkableComponentType
      representationData={representationData}
      label={label}
    />
    <Icon iconName={IconName.ARROW_FORWARD} iconSize={IconSize.MEDIUM} />
    <Label>
      <TruncatedTextPopover>
        {metaDataReference?.displayLabel}
      </TruncatedTextPopover>
    </Label>
    <Icon iconName={IconName.ARROW_FORWARD} iconSize={IconSize.MEDIUM} />
    <ComponentTypeWithoutLabel
      representationData={contextRepresentationData}
      isSelected={true}
      label={contextLabel}
    />
  </FlexBox>
);

const ShrinkableComponentType = (props: ComponentTypeProps) => (
  <FlexBox minWidth="48px">
    <ComponentType {...props} />
  </FlexBox>
);

const ComponentTypeWithoutLabel = ({
  representationData,
  isSelected,
  label,
}: ComponentTypeProps) => {
  if (representationData.value === 'none') {
    return (
      <WithPopover content={label}>
        <IconWrapper
          $backgroundColor={representationData.lightenedColor}
          $borderColor={representationData.contrastColor}
        >
          <Icon
            iconSize={IconSize.SMALL}
            iconName={IconName.COMPONENT}
            color={representationData.contrastColor}
          />
        </IconWrapper>
      </WithPopover>
    );
  }

  return (
    <>
      <ComponentType
        label=""
        representationData={representationData}
        isSelected={isSelected}
      />
    </>
  );
};
